import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSearch, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Shield,
  Plus,
  Pencil,
  Archive,
  ArchiveRestore,
  Loader2,
  ListChecks,
  ChevronUp,
  ChevronDown,
  Trash2,
  UserPlus,
  GripVertical,
  FileText,
  Copy,
  Settings2,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { getBoardRoutePath, getBoardTypeConfig, BOARD_TYPES, BOARD_TYPE_VALUES } from "@/lib/board-types";
import { useAuth } from "@/lib/auth-context";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ExportSettings, DEFAULT_EXPORT_SETTINGS } from "@/lib/export-settings";
import { usePageTitle } from "@/hooks/use-page-title";
import { MyBoardsGrid, type BoardSummary } from "@/components/tasks/my-boards-grid";

interface Board {
  id: string;
  name: string;
  description: string | null;
  type: string;
  isArchived: boolean;
  createdAt: string;
}

interface Status {
  id: string;
  boardId: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  isArchived: boolean;
}

interface Label {
  id: string;
  boardId: string;
  name: string;
  sortOrder: number;
  isArchived: boolean;
  color: string | null;
}

interface Member {
  id: string;
  boardId: string;
  userId: string;
  role: string;
  username: string;
  email: string;
}

interface AvailableUser {
  id: string;
  username: string;
  email: string;
  isAdmin: boolean;
  isActive: boolean;
}

const boardFormSchema = z.object({
  name: z.string().min(1, "Board name is required").max(200),
  description: z.string().max(2000).optional(),
  type: z.enum(BOARD_TYPE_VALUES).default("regular"),
});

const statusFormSchema = z.object({
  name: z.string().min(1, "Status name is required").max(100),
  isDefault: z.boolean().optional(),
});

const labelFormSchema = z.object({
  name: z.string().min(1, "Label name is required").max(100),
});

const memberFormSchema = z.object({
  userId: z.string().min(1, "Select a user"),
  role: z.enum(["admin", "editor", "viewer"]),
});

export default function TaskBoardAdmin() {
  usePageTitle("Task Board Admin");
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const searchString = useSearch();

  const [, setLocation] = useLocation();

  const initialBoardId = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return params.get("boardId") || null;
  }, []);

  const initialTab = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const tab = params.get("tab");
    if (tab && ["statuses", "labels", "members", "templates"].includes(tab)) return tab;
    return "statuses";
  }, []);

  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(initialBoardId);
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const urlBoardId = params.get("boardId") || null;
    const urlTab = params.get("tab");
    const resolvedTab = urlTab && ["statuses", "labels", "members", "templates"].includes(urlTab) ? urlTab : "statuses";
    if (urlBoardId !== selectedBoardId) setSelectedBoardId(urlBoardId);
    if (resolvedTab !== activeTab) setActiveTab(resolvedTab);
  }, [searchString]);

  function updateAdminUrl(boardId: string | null, tab: string, replace = true) {
    const params = new URLSearchParams();
    if (boardId) params.set("boardId", boardId);
    if (tab && tab !== "statuses") params.set("tab", tab);
    const qs = params.toString();
    setLocation(qs ? `/admin/tasks?${qs}` : "/admin/tasks", { replace });
  }

  function handleBoardChange(val: string) {
    const boardId = val || null;
    setSelectedBoardId(boardId);
    setActiveTab("statuses");
    updateAdminUrl(boardId, "statuses");
  }

  function handleTabChange(tab: string) {
    setActiveTab(tab);
    updateAdminUrl(selectedBoardId, tab);
  }

  const [boardSheetOpen, setBoardSheetOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);
  const [duplicateTargetBoardId, setDuplicateTargetBoardId] = useState<string | null>(null);
  const [duplicateBoardSheetOpen, setDuplicateBoardSheetOpen] = useState(false);
  const [dupIncludeStatuses, setDupIncludeStatuses] = useState(true);
  const [dupIncludeLabels, setDupIncludeLabels] = useState(true);
  const [dupIncludeMembers, setDupIncludeMembers] = useState(true);
  const [dupIncludeTemplates, setDupIncludeTemplates] = useState(true);

  const { data: boardsData, isLoading: boardsLoading } = useQuery<{ boards: Board[] }>({
    queryKey: ["/api/task-boards"],
  });

  const { data: adminSummariesData, isLoading: adminSummariesLoading } = useQuery<{ boards: BoardSummary[] }>({
    queryKey: ["/api/task-boards/summaries", "admin"],
    queryFn: async () => {
      const res = await fetch("/api/task-boards/summaries?admin=true", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch admin summaries");
      return res.json();
    },
    enabled: !selectedBoardId,
  });

  const selectedBoard = boardsData?.boards.find(b => b.id === selectedBoardId) ?? null;

  useEffect(() => {
    if (initialBoardId && boardsData && !boardsData.boards.some(b => b.id === initialBoardId)) {
      setSelectedBoardId(null);
    }
  }, [boardsData, initialBoardId]);

  const { data: statusesData, isLoading: statusesLoading } = useQuery<{ statuses: Status[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "statuses"],
    enabled: !!selectedBoardId,
    queryFn: async () => {
      const res = await fetch(`/api/task-boards/${selectedBoardId}/statuses`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch statuses");
      return res.json();
    },
  });

  const { data: labelsData, isLoading: labelsLoading } = useQuery<{ labels: Label[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "labels"],
    enabled: !!selectedBoardId,
    queryFn: async () => {
      const res = await fetch(`/api/task-boards/${selectedBoardId}/labels`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch labels");
      return res.json();
    },
  });

  const { data: membersData, isLoading: membersLoading } = useQuery<{ members: Member[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "members"],
    enabled: !!selectedBoardId,
    queryFn: async () => {
      const res = await fetch(`/api/task-boards/${selectedBoardId}/members`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch members");
      return res.json();
    },
  });

  const { data: usersData } = useQuery<{ users: AvailableUser[] }>({
    queryKey: ["/api/task-boards/available-users"],
  });

  interface Template {
    id: string;
    boardId: string;
    title: string;
    description: string | null;
    type: string;
    isTemplate: boolean;
    createdAt: string;
    updatedAt: string;
    archivedAt: string | null;
    exportSettings: ExportSettings | null;
  }

  const { data: templatesData, isLoading: templatesLoading } = useQuery<{ templates: Template[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "templates"],
    enabled: !!selectedBoardId,
    queryFn: async () => {
      const res = await fetch(`/api/task-boards/${selectedBoardId}/templates`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch templates");
      return res.json();
    },
  });

  // ── Board mutations ──

  const createBoardMutation = useMutation({
    mutationFn: async (data: z.infer<typeof boardFormSchema>) => {
      return apiRequest("POST", "/api/task-boards", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards"] });
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards/summaries"] });
      setBoardSheetOpen(false);
      setEditingBoard(null);
      toast({ title: "Board created", description: "Default statuses have been added automatically." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateBoardMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Record<string, unknown>) => {
      return apiRequest("PATCH", `/api/task-boards/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards"] });
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards/summaries"] });
      setBoardSheetOpen(false);
      setEditingBoard(null);
      toast({ title: "Board updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const duplicateBoardMutation = useMutation({
    mutationFn: async (data: { boardId: string; includeStatuses: boolean; includeLabels: boolean; includeMembers: boolean; includeTemplates: boolean }) => {
      return apiRequest("POST", `/api/task-boards/${data.boardId}/duplicate`, {
        includeStatuses: data.includeStatuses,
        includeLabels: data.includeLabels,
        includeMembers: data.includeMembers,
        includeTemplates: data.includeTemplates,
      });
    },
    onSuccess: (data: { board: { id: string; name: string } }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards"] });
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards/summaries"] });
      setDuplicateBoardSheetOpen(false);
      const newBoardId = data.board.id;
      if (newBoardId) {
        setSelectedBoardId(newBoardId);
        updateAdminUrl(newBoardId, activeTab);
      }
      toast({ title: "Board duplicated", description: `New board "${data.board.name}" has been created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ── Status mutations ──

  const createStatusMutation = useMutation({
    mutationFn: async (data: z.infer<typeof statusFormSchema>) => {
      return apiRequest("POST", `/api/task-boards/${selectedBoardId}/statuses`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "statuses"] });
      setStatusDialogOpen(false);
      setEditingStatus(null);
      toast({ title: "Status created" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Record<string, unknown>) => {
      return apiRequest("PATCH", `/api/task-boards/${selectedBoardId}/statuses/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "statuses"] });
      setStatusDialogOpen(false);
      setEditingStatus(null);
      toast({ title: "Status updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const reorderStatusesMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      return apiRequest("POST", `/api/task-boards/${selectedBoardId}/statuses/reorder`, { orderedIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "statuses"] });
    },
  });

  // ── Label mutations ──

  const createLabelMutation = useMutation({
    mutationFn: async (data: z.infer<typeof labelFormSchema>) => {
      return apiRequest("POST", `/api/task-boards/${selectedBoardId}/labels`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "labels"] });
      setLabelDialogOpen(false);
      setEditingLabel(null);
      toast({ title: "Label created" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateLabelMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Record<string, unknown>) => {
      return apiRequest("PATCH", `/api/task-boards/${selectedBoardId}/labels/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "labels"] });
      setLabelDialogOpen(false);
      setEditingLabel(null);
      toast({ title: "Label updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ── Member mutations ──

  const addMemberMutation = useMutation({
    mutationFn: async (data: z.infer<typeof memberFormSchema>) => {
      return apiRequest("POST", `/api/task-boards/${selectedBoardId}/members`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "members"] });
      setMemberDialogOpen(false);
      toast({ title: "Member added" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      return apiRequest("PATCH", `/api/task-boards/${selectedBoardId}/members/${memberId}`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "members"] });
      toast({ title: "Member role updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      return apiRequest("DELETE", `/api/task-boards/${selectedBoardId}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "members"] });
      toast({ title: "Member removed" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ── Template mutations ──

  const createTemplateMutation = useMutation({
    mutationFn: async (data: { title: string; description?: string }) => {
      return apiRequest("POST", "/api/tasks", {
        boardId: selectedBoardId,
        title: data.title,
        description: data.description,
        isTemplate: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "templates"] });
      setTemplateDialogOpen(false);
      toast({ title: "Template created" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return apiRequest("PATCH", `/api/tasks/${taskId}/archive`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "templates"] });
      toast({ title: "Template archived" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const duplicateTemplateMutation = useMutation({
    mutationFn: async ({ templateId, targetBoardId }: { templateId: string; targetBoardId?: string }) => {
      return apiRequest("POST", `/api/tasks/from-template/${templateId}/duplicate`, {
        ...(targetBoardId ? { targetBoardId } : {}),
      });
    },
    onSuccess: (_data, variables) => {
      const targetId = variables.targetBoardId ?? selectedBoardId;
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", targetId, "templates"] });
      if (variables.targetBoardId && variables.targetBoardId !== selectedBoardId) {
        queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "templates"] });
      }
      setDuplicateDialogOpen(false);
      setDuplicatingTemplateId(null);
      setDuplicateTargetBoardId(null);
      toast({ title: "Template duplicated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const [exportSettingsOpen, setExportSettingsOpen] = useState(false);
  const [exportSettingsTemplateId, setExportSettingsTemplateId] = useState<string | null>(null);
  const [exportSettingsData, setExportSettingsData] = useState<ExportSettings>(DEFAULT_EXPORT_SETTINGS);

  const exportSettingsMutation = useMutation({
    mutationFn: async ({ taskId, exportSettings }: { taskId: string; exportSettings: ExportSettings }) => {
      return apiRequest("PATCH", `/api/tasks/${taskId}`, { exportSettings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/task-boards", selectedBoardId, "templates"] });
      toast({ title: "Export settings saved" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleOpenExportSettings = (template: Template) => {
    setExportSettingsTemplateId(template.id);
    setExportSettingsData(template.exportSettings ?? DEFAULT_EXPORT_SETTINGS);
    setExportSettingsOpen(true);
  };

  const handleExportSettingsToggle = (category: "formats" | "sections", key: string, value: boolean) => {
    setExportSettingsData(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: value },
    }));
  };

  const handleSaveExportSettings = () => {
    if (!exportSettingsTemplateId) return;
    exportSettingsMutation.mutate({ taskId: exportSettingsTemplateId, exportSettings: exportSettingsData });
  };

  const handleDuplicateClick = (templateId: string) => {
    setDuplicatingTemplateId(templateId);
    setDuplicateTargetBoardId(selectedBoardId);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = () => {
    if (!duplicatingTemplateId) return;
    const targetBoardId = duplicateTargetBoardId !== selectedBoardId ? (duplicateTargetBoardId ?? undefined) : undefined;
    duplicateTemplateMutation.mutate({ templateId: duplicatingTemplateId, targetBoardId });
  };

  // ── Reorder helpers ──

  const handleMoveStatus = (statusId: string, direction: "up" | "down") => {
    const statuses = statusesData?.statuses.filter(s => !s.isArchived) ?? [];
    const idx = statuses.findIndex(s => s.id === statusId);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= statuses.length) return;
    const reordered = [...statuses];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    reorderStatusesMutation.mutate(reordered.map(s => s.id));
  };

  // ── Access check ──

  if (!currentUser?.isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-6 pt-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2" data-testid="text-access-denied-title">
                Not Authorized
              </h2>
              <p className="text-muted-foreground mb-6" data-testid="text-access-denied-message">
                This page is only available to administrators.
              </p>
              <Button asChild data-testid="button-go-to-dashboard">
                <a href="/dashboard">Go to Dashboard</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loading ──

  if (boardsLoading) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Task Boards" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-task-boards">
              <ListChecks className="w-6 h-6 text-primary" />
              Task Boards
            </span>
          }
        />
        <div className="space-y-4 mt-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const boards = boardsData?.boards ?? [];
  const activeStatuses = statusesData?.statuses.filter(s => !s.isArchived) ?? [];
  const archivedStatuses = statusesData?.statuses.filter(s => s.isArchived) ?? [];
  const activeLabels = labelsData?.labels.filter(l => !l.isArchived) ?? [];
  const archivedLabels = labelsData?.labels.filter(l => l.isArchived) ?? [];
  const members = membersData?.members ?? [];
  const existingMemberUserIds = new Set(members.map(m => m.userId));
  const availableUsersForBoard = (usersData?.users ?? []).filter(u => !existingMemberUserIds.has(u.id));

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={
          selectedBoard
            ? [
                { label: "Administration" },
                {
                  label: "Task Boards",
                  href: "/admin/tasks",
                  onClick: (e: React.MouseEvent) => {
                    e.preventDefault();
                    setSelectedBoardId(null);
                    updateAdminUrl(null, "statuses");
                  },
                },
                { label: selectedBoard.name },
              ]
            : [{ label: "Administration" }, { label: "Task Boards" }]
        }
        title={
          <span className="flex items-center gap-2" data-testid="heading-task-boards">
            <ListChecks className="w-6 h-6 text-primary" />
            {selectedBoard ? selectedBoard.name : "Task Boards"}
          </span>
        }
        actions={
          <Button onClick={() => { setEditingBoard(null); setBoardSheetOpen(true); }} data-testid="button-create-board">
            <Plus className="w-4 h-4 mr-2" />
            Create Board
          </Button>
        }
      />

      {!selectedBoardId && (
        <p className="text-sm text-muted-foreground -mt-3" data-testid="text-admin-subtitle">
          Manage all task boards, statuses, labels, members, and templates
        </p>
      )}

      <div className="mt-4 space-y-6">
        {!selectedBoardId ? (
          /* ── Admin Landing Grid ── */
          (() => {
            const allSummaries = adminSummariesData?.boards ?? [];
            const activeBoards = allSummaries.filter(b => !b.isArchived);
            const archivedBoards = allSummaries.filter(b => b.isArchived);

            const boardsByType = Object.keys(BOARD_TYPES).reduce<Record<string, typeof activeBoards>>((acc, type) => {
              const boards = activeBoards.filter(b => (b.type || "regular") === type);
              if (boards.length > 0) acc[type] = boards;
              return acc;
            }, {});

            const handleManageBoard = (boardId: string) => {
              setSelectedBoardId(boardId);
              setActiveTab("statuses");
              updateAdminUrl(boardId, "statuses", false);
            };
            const handleViewBoard = (boardId: string, boardType: string) => {
              const routePath = getBoardRoutePath(boardType);
              setLocation(`${routePath}?boardId=${boardId}`);
            };
            const handleManageMembers = (boardId: string) => {
              setSelectedBoardId(boardId);
              setActiveTab("members");
              updateAdminUrl(boardId, "members", false);
            };

            return (
              <div className="space-y-8" data-testid="admin-boards-landing">
                {adminSummariesLoading ? (
                  <MyBoardsGrid
                    boards={[]}
                    isLoading
                    onSelectBoard={() => {}}
                    adminMode
                    emptyMessage=""
                  />
                ) : activeBoards.length === 0 && archivedBoards.length === 0 ? (
                  <MyBoardsGrid
                    boards={[]}
                    isLoading={false}
                    onSelectBoard={() => {}}
                    adminMode
                    emptyMessage="No boards have been created yet."
                  />
                ) : (
                  Object.entries(boardsByType).map(([type, boards]) => {
                    const config = getBoardTypeConfig(type);
                    const TypeIcon = config.icon;
                    return (
                      <div key={type} className="space-y-3" data-testid={`admin-board-group-${type}`}>
                        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2" data-testid={`heading-board-type-${type}`}>
                          <TypeIcon className="h-4 w-4" />
                          {config.labels.plural}
                        </h3>
                        <MyBoardsGrid
                          boards={boards}
                          isLoading={false}
                          onSelectBoard={() => {}}
                          adminMode
                          onManageBoard={handleManageBoard}
                          onViewBoard={handleViewBoard}
                          onManageMembers={handleManageMembers}
                          emptyMessage=""
                        />
                      </div>
                    );
                  })
                )}
                {archivedBoards.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2" data-testid="heading-archived-boards">
                      <Archive className="h-4 w-4" />
                      Archived Boards
                    </h3>
                    <MyBoardsGrid
                      boards={archivedBoards}
                      isLoading={false}
                      onSelectBoard={() => {}}
                      adminMode
                      onManageBoard={handleManageBoard}
                      onViewBoard={handleViewBoard}
                      onManageMembers={handleManageMembers}
                      emptyMessage=""
                    />
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          /* ── Board Detail View ── */
          <>
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={selectedBoardId ?? ""}
            onValueChange={handleBoardChange}
          >
            <SelectTrigger className="w-72" data-testid="select-board">
              <SelectValue placeholder="Select a board..." />
            </SelectTrigger>
            <SelectContent>
              {boards.map(board => (
                <SelectItem key={board.id} value={board.id} data-testid={`select-board-option-${board.id}`}>
                  <span className="flex items-center gap-2">
                    {board.name}
                    {board.type && board.type !== "regular" && (
                      <Badge variant="outline" className="text-xs capitalize" data-testid={`badge-board-type-${board.id}`}>{board.type}</Badge>
                    )}
                    {board.isArchived && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedBoard && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditingBoard(selectedBoard); setBoardSheetOpen(true); }}
                data-testid="button-edit-board"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDupIncludeStatuses(true);
                  setDupIncludeLabels(true);
                  setDupIncludeMembers(true);
                  setDupIncludeTemplates(true);
                  setDuplicateBoardSheetOpen(true);
                }}
                disabled={selectedBoard.isArchived || duplicateBoardMutation.isPending}
                data-testid="button-duplicate-board"
              >
                <Copy className="w-4 h-4 mr-1" />
                Duplicate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateBoardMutation.mutate({ id: selectedBoard.id, isArchived: !selectedBoard.isArchived })}
                disabled={updateBoardMutation.isPending}
                data-testid="button-toggle-archive-board"
              >
                {selectedBoard.isArchived ? (
                  <><ArchiveRestore className="w-4 h-4 mr-1" /> Unarchive</>
                ) : (
                  <><Archive className="w-4 h-4 mr-1" /> Archive</>
                )}
              </Button>
            </div>
          )}
        </div>

        {boards.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <ListChecks className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-1" data-testid="text-no-boards">No boards yet</h3>
              <p className="text-muted-foreground text-sm mb-4">Create your first board to get started with task management.</p>
              <Button onClick={() => { setEditingBoard(null); setBoardSheetOpen(true); }} data-testid="button-create-first-board">
                <Plus className="w-4 h-4 mr-2" />
                Create Board
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Board management tabs */}
        {selectedBoard && (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList data-testid="tabs-board-management">
              <TabsTrigger value="statuses" data-testid="tab-statuses">Statuses</TabsTrigger>
              <TabsTrigger value="labels" data-testid="tab-labels">Labels</TabsTrigger>
              <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
              <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
            </TabsList>

            {/* ── Statuses Tab ── */}
            <TabsContent value="statuses">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle>Statuses</CardTitle>
                    <CardDescription>Manage kanban columns for this board. Drag to reorder.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => { setEditingStatus(null); setStatusDialogOpen(true); }} data-testid="button-add-status">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Status
                  </Button>
                </CardHeader>
                <CardContent>
                  {statusesLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">Order</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead className="w-24">Default</TableHead>
                            <TableHead className="w-32 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeStatuses.map((status, idx) => (
                            <TableRow key={status.id} data-testid={`row-status-${status.id}`}>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5"
                                    disabled={idx === 0 || reorderStatusesMutation.isPending}
                                    onClick={() => handleMoveStatus(status.id, "up")}
                                    data-testid={`button-move-status-up-${status.id}`}
                                  >
                                    <ChevronUp className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5"
                                    disabled={idx === activeStatuses.length - 1 || reorderStatusesMutation.isPending}
                                    onClick={() => handleMoveStatus(status.id, "down")}
                                    data-testid={`button-move-status-down-${status.id}`}
                                  >
                                    <ChevronDown className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium" data-testid={`text-status-name-${status.id}`}>
                                {status.name}
                              </TableCell>
                              <TableCell>
                                {status.isDefault && <Badge variant="default" data-testid={`badge-default-${status.id}`}>Default</Badge>}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => { setEditingStatus(status); setStatusDialogOpen(true); }}
                                    data-testid={`button-edit-status-${status.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  {!status.isDefault && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => updateStatusMutation.mutate({ id: status.id, isArchived: true })}
                                      disabled={updateStatusMutation.isPending}
                                      data-testid={`button-archive-status-${status.id}`}
                                    >
                                      <Archive className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {archivedStatuses.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Archived Statuses</h4>
                          <Table>
                            <TableBody>
                              {archivedStatuses.map(status => (
                                <TableRow key={status.id} className="opacity-60" data-testid={`row-archived-status-${status.id}`}>
                                  <TableCell className="font-medium">{status.name}</TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateStatusMutation.mutate({ id: status.id, isArchived: false })}
                                      disabled={updateStatusMutation.isPending}
                                      data-testid={`button-unarchive-status-${status.id}`}
                                    >
                                      <ArchiveRestore className="h-4 w-4 mr-1" />
                                      Restore
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Labels Tab ── */}
            <TabsContent value="labels">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle>Labels</CardTitle>
                    <CardDescription>Create labels that can be applied to tasks on this board.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => { setEditingLabel(null); setLabelDialogOpen(true); }} data-testid="button-add-label">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Label
                  </Button>
                </CardHeader>
                <CardContent>
                  {labelsLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  ) : activeLabels.length === 0 && archivedLabels.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-labels">
                      No labels yet. Add a label to categorize tasks.
                    </p>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead className="w-32 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeLabels.map(label => (
                            <TableRow key={label.id} data-testid={`row-label-${label.id}`}>
                              <TableCell className="font-medium" data-testid={`text-label-name-${label.id}`}>
                                {label.name}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => { setEditingLabel(label); setLabelDialogOpen(true); }}
                                    data-testid={`button-edit-label-${label.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => updateLabelMutation.mutate({ id: label.id, isArchived: true })}
                                    disabled={updateLabelMutation.isPending}
                                    data-testid={`button-archive-label-${label.id}`}
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {archivedLabels.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Archived Labels</h4>
                          <Table>
                            <TableBody>
                              {archivedLabels.map(label => (
                                <TableRow key={label.id} className="opacity-60" data-testid={`row-archived-label-${label.id}`}>
                                  <TableCell className="font-medium">{label.name}</TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateLabelMutation.mutate({ id: label.id, isArchived: false })}
                                      disabled={updateLabelMutation.isPending}
                                      data-testid={`button-unarchive-label-${label.id}`}
                                    >
                                      <ArchiveRestore className="h-4 w-4 mr-1" />
                                      Restore
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Members Tab ── */}
            <TabsContent value="members">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle>Members</CardTitle>
                    <CardDescription>Manage who has access to this board and their roles.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setMemberDialogOpen(true)} data-testid="button-add-member">
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Member
                  </Button>
                </CardHeader>
                <CardContent>
                  {membersLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  ) : members.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-members">
                      No members yet. Add users to give them access to this board.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead className="w-40">Role</TableHead>
                          <TableHead className="w-20 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map(member => (
                          <TableRow key={member.id} data-testid={`row-member-${member.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium" data-testid={`text-member-username-${member.id}`}>{member.username}</p>
                                <p className="text-sm text-muted-foreground">{member.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={member.role}
                                onValueChange={(role) => updateMemberMutation.mutate({ memberId: member.id, role })}
                                disabled={updateMemberMutation.isPending}
                              >
                                <SelectTrigger data-testid={`select-member-role-${member.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="editor">Editor</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeMemberMutation.mutate(member.id)}
                                disabled={removeMemberMutation.isPending}
                                data-testid={`button-remove-member-${member.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Templates Tab ── */}
            <TabsContent value="templates">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle>Templates</CardTitle>
                    <CardDescription>Manage reusable task templates for this board.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setTemplateDialogOpen(true)} data-testid="button-add-template">
                    <Plus className="w-4 h-4 mr-1" />
                    New Template
                  </Button>
                </CardHeader>
                <CardContent>
                  {templatesLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  ) : (templatesData?.templates ?? []).length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground" data-testid="text-no-templates">
                        No templates yet. Create a template to speed up task creation.
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead className="w-32">Created</TableHead>
                          <TableHead className="w-36">Last Updated</TableHead>
                          <TableHead className="w-36 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(templatesData?.templates ?? []).map(template => (
                          <TableRow key={template.id} data-testid={`row-template-${template.id}`}>
                            <TableCell>
                              <p className="font-medium" data-testid={`text-template-title-${template.id}`}>{template.title}</p>
                              {template.description && (
                                <p className="text-sm text-muted-foreground line-clamp-1">{template.description}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(template.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-template-updated-${template.id}`}>
                              {new Date(template.updatedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  asChild
                                  data-testid={`button-edit-template-${template.id}`}
                                >
                                  <a href={`${getBoardRoutePath(selectedBoard?.type)}/${template.id}`}>
                                    <Pencil className="h-4 w-4" />
                                  </a>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenExportSettings(template)}
                                  title="Export settings"
                                  data-testid={`button-export-settings-${template.id}`}
                                >
                                  <Settings2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDuplicateClick(template.id)}
                                  disabled={duplicateTemplateMutation.isPending}
                                  data-testid={`button-duplicate-template-${template.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => archiveTemplateMutation.mutate(template.id)}
                                  disabled={archiveTemplateMutation.isPending}
                                  data-testid={`button-archive-template-${template.id}`}
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
          </>
        )}
      </div>

      {/* ── Board Create/Edit Sheet ── */}
      <BoardFormSheet
        open={boardSheetOpen}
        onOpenChange={(open) => { setBoardSheetOpen(open); if (!open) setEditingBoard(null); }}
        board={editingBoard}
        onSubmit={(data) => {
          if (editingBoard) {
            updateBoardMutation.mutate({ id: editingBoard.id, ...data });
          } else {
            createBoardMutation.mutate(data);
          }
        }}
        isPending={createBoardMutation.isPending || updateBoardMutation.isPending}
      />

      {/* ── Duplicate Board Sheet ── */}
      <Sheet open={duplicateBoardSheetOpen} onOpenChange={(open) => { setDuplicateBoardSheetOpen(open); }}>
        <SheetContent side="right" data-testid="sheet-duplicate-board">
          <SheetHeader>
            <SheetTitle>Duplicate Board</SheetTitle>
            <SheetDescription>
              Create a copy of "{selectedBoard?.name}". The new board will be named "Copy of {selectedBoard?.name}".
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <p className="text-sm font-medium">Select elements to include:</p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="checkbox-include-statuses">
                <Checkbox
                  checked={dupIncludeStatuses}
                  onCheckedChange={(checked) => {
                    setDupIncludeStatuses(!!checked);
                    if (!checked) setDupIncludeTemplates(false);
                  }}
                />
                <div>
                  <span className="text-sm font-medium">Statuses</span>
                  <p className="text-xs text-muted-foreground">Copy all active statuses (columns)</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="checkbox-include-labels">
                <Checkbox
                  checked={dupIncludeLabels}
                  onCheckedChange={(checked) => setDupIncludeLabels(!!checked)}
                />
                <div>
                  <span className="text-sm font-medium">Labels</span>
                  <p className="text-xs text-muted-foreground">Copy all active labels</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="checkbox-include-members">
                <Checkbox
                  checked={dupIncludeMembers}
                  onCheckedChange={(checked) => setDupIncludeMembers(!!checked)}
                />
                <div>
                  <span className="text-sm font-medium">Members</span>
                  <p className="text-xs text-muted-foreground">Copy all board members with their roles</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 ${dupIncludeStatuses ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`} data-testid="checkbox-include-templates">
                <Checkbox
                  checked={dupIncludeTemplates}
                  onCheckedChange={(checked) => setDupIncludeTemplates(!!checked)}
                  disabled={!dupIncludeStatuses}
                />
                <div>
                  <span className="text-sm font-medium">Templates</span>
                  <p className="text-xs text-muted-foreground">
                    {dupIncludeStatuses
                      ? "Copy all template tasks with their checklists and labels"
                      : "Requires statuses to be included"}
                  </p>
                </div>
              </label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setDuplicateBoardSheetOpen(false)}
                data-testid="button-cancel-duplicate-board"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!selectedBoardId) return;
                  duplicateBoardMutation.mutate({
                    boardId: selectedBoardId,
                    includeStatuses: dupIncludeStatuses,
                    includeLabels: dupIncludeLabels,
                    includeMembers: dupIncludeMembers,
                    includeTemplates: dupIncludeTemplates,
                  });
                }}
                disabled={duplicateBoardMutation.isPending}
                data-testid="button-confirm-duplicate-board"
              >
                {duplicateBoardMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Duplicate
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Status Create/Edit Dialog ── */}
      <StatusFormDialog
        open={statusDialogOpen}
        onOpenChange={(open) => { setStatusDialogOpen(open); if (!open) setEditingStatus(null); }}
        status={editingStatus}
        onSubmit={(data) => {
          if (editingStatus) {
            updateStatusMutation.mutate({ id: editingStatus.id, ...data });
          } else {
            createStatusMutation.mutate(data);
          }
        }}
        isPending={createStatusMutation.isPending || updateStatusMutation.isPending}
      />

      {/* ── Label Create/Edit Dialog ── */}
      <LabelFormDialog
        open={labelDialogOpen}
        onOpenChange={(open) => { setLabelDialogOpen(open); if (!open) setEditingLabel(null); }}
        label={editingLabel}
        onSubmit={(data) => {
          if (editingLabel) {
            updateLabelMutation.mutate({ id: editingLabel.id, ...data });
          } else {
            createLabelMutation.mutate(data);
          }
        }}
        isPending={createLabelMutation.isPending || updateLabelMutation.isPending}
      />

      {/* ── Add Member Dialog ── */}
      <MemberFormDialog
        open={memberDialogOpen}
        onOpenChange={setMemberDialogOpen}
        availableUsers={availableUsersForBoard}
        onSubmit={(data) => addMemberMutation.mutate(data)}
        isPending={addMemberMutation.isPending}
      />

      {/* ── New Template Dialog ── */}
      <TemplateFormDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        onSubmit={(data) => createTemplateMutation.mutate(data)}
        isPending={createTemplateMutation.isPending}
      />

      {/* ── Duplicate Template Dialog ── */}
      <Dialog open={duplicateDialogOpen} onOpenChange={(open) => {
        setDuplicateDialogOpen(open);
        if (!open) {
          setDuplicatingTemplateId(null);
          setDuplicateTargetBoardId(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Template</DialogTitle>
            <DialogDescription>
              Choose which board to duplicate this template to. The copy will be named "Copy of" followed by the original title.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">Target Board</label>
            <Select
              value={duplicateTargetBoardId ?? ""}
              onValueChange={(val) => setDuplicateTargetBoardId(val)}
            >
              <SelectTrigger data-testid="select-duplicate-target-board">
                <SelectValue placeholder="Select a board" />
              </SelectTrigger>
              <SelectContent>
                {(boardsData?.boards ?? [])
                  .filter(b => !b.isArchived)
                  .map(b => (
                    <SelectItem key={b.id} value={b.id} data-testid={`option-board-${b.id}`}>
                      {b.name}{b.id === selectedBoardId ? " (current)" : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateDialogOpen(false)}
              data-testid="button-cancel-duplicate"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDuplicateConfirm}
              disabled={!duplicateTargetBoardId || duplicateTemplateMutation.isPending}
              data-testid="button-confirm-duplicate"
            >
              {duplicateTemplateMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Export Settings Sheet ── */}
      <Sheet open={exportSettingsOpen} onOpenChange={(open) => {
        setExportSettingsOpen(open);
        if (!open) setExportSettingsTemplateId(null);
      }}>
        <SheetContent side="right" data-testid="drawer-export-settings">
          <SheetHeader>
            <SheetTitle>Export Settings</SheetTitle>
            <SheetDescription>
              {(templatesData?.templates ?? []).find(t => t.id === exportSettingsTemplateId)?.title ?? "Template"}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Export Formats</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="fmt-copyLink" className="text-sm">Copy Link</Label>
                  <Switch
                    id="fmt-copyLink"
                    checked={exportSettingsData.formats.copyLink}
                    onCheckedChange={(v) => handleExportSettingsToggle("formats", "copyLink", v)}
                    data-testid="switch-format-copyLink"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="fmt-copyMarkdown" className="text-sm">Copy as Markdown</Label>
                  <Switch
                    id="fmt-copyMarkdown"
                    checked={exportSettingsData.formats.copyMarkdown}
                    onCheckedChange={(v) => handleExportSettingsToggle("formats", "copyMarkdown", v)}
                    data-testid="switch-format-copyMarkdown"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="fmt-downloadPdf" className="text-sm">Download PDF</Label>
                  <Switch
                    id="fmt-downloadPdf"
                    checked={exportSettingsData.formats.downloadPdf}
                    onCheckedChange={(v) => handleExportSettingsToggle("formats", "downloadPdf", v)}
                    data-testid="switch-format-downloadPdf"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Exportable Sections</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-date" className="text-sm">Date</Label>
                  <Switch
                    id="sec-date"
                    checked={exportSettingsData.sections.date}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "date", v)}
                    data-testid="switch-section-date"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-metadata" className="text-sm">Metadata (status, assignee, priority, labels)</Label>
                  <Switch
                    id="sec-metadata"
                    checked={exportSettingsData.sections.metadata}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "metadata", v)}
                    data-testid="switch-section-metadata"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-description" className="text-sm">Description</Label>
                  <Switch
                    id="sec-description"
                    checked={exportSettingsData.sections.description}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "description", v)}
                    data-testid="switch-section-description"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-agendaItems" className="text-sm">Agenda Items</Label>
                  <Switch
                    id="sec-agendaItems"
                    checked={exportSettingsData.sections.agendaItems}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "agendaItems", v)}
                    data-testid="switch-section-agendaItems"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-animals" className="text-sm">Animals</Label>
                  <Switch
                    id="sec-animals"
                    checked={exportSettingsData.sections.animals}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "animals", v)}
                    data-testid="switch-section-animals"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-interactions" className="text-sm">Interactions</Label>
                  <Switch
                    id="sec-interactions"
                    checked={exportSettingsData.sections.interactions}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "interactions", v)}
                    data-testid="switch-section-interactions"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="sec-notes" className="text-sm">Notes</Label>
                  <Switch
                    id="sec-notes"
                    checked={exportSettingsData.sections.notes}
                    onCheckedChange={(v) => handleExportSettingsToggle("sections", "notes", v)}
                    data-testid="switch-section-notes"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <Button
              onClick={handleSaveExportSettings}
              disabled={exportSettingsMutation.isPending}
              className="w-full"
              data-testid="button-save-export-settings"
            >
              {exportSettingsMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Save Settings
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function BoardFormSheet({ open, onOpenChange, board, onSubmit, isPending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: Board | null;
  onSubmit: (data: z.infer<typeof boardFormSchema>) => void;
  isPending: boolean;
}) {
  const form = useForm<z.infer<typeof boardFormSchema>>({
    resolver: zodResolver(boardFormSchema),
    defaultValues: { name: "", description: "", type: "regular" as const },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: board?.name ?? "", description: board?.description ?? "", type: (board?.type as "regular" | "meeting" | "evaluation") ?? "regular" });
    }
  }, [open, board]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="sheet-board-form">
        <SheetHeader>
          <SheetTitle>{board ? "Edit Board" : "Create Board"}</SheetTitle>
          <SheetDescription>
            {board ? "Update the board details." : "Create a new task board. Three default statuses (To Do, In Progress, Done) will be created automatically."}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Board name" maxLength={200} data-testid="input-board-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Optional description" maxLength={2000} data-testid="input-board-description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Board Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-board-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="regular" data-testid="select-board-type-regular">Regular</SelectItem>
                      <SelectItem value="meeting" data-testid="select-board-type-meeting">Meeting</SelectItem>
                      <SelectItem value="evaluation" data-testid="select-board-type-evaluation">Evaluation</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isPending} data-testid="button-submit-board">
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {board ? "Save Changes" : "Create Board"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

function StatusFormDialog({ open, onOpenChange, status, onSubmit, isPending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: Status | null;
  onSubmit: (data: z.infer<typeof statusFormSchema>) => void;
  isPending: boolean;
}) {
  const form = useForm<z.infer<typeof statusFormSchema>>({
    resolver: zodResolver(statusFormSchema),
    defaultValues: { name: "", isDefault: false },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: status?.name ?? "", isDefault: status?.isDefault ?? false });
    }
  }, [open, status]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-status-form">
        <DialogHeader>
          <DialogTitle>{status ? "Edit Status" : "Add Status"}</DialogTitle>
          <DialogDescription>
            {status ? "Update the status name or default setting." : "Add a new status column for this board."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Status name" data-testid="input-status-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4"
                      data-testid="checkbox-status-default"
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">Set as default status for new tasks</FormLabel>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isPending} data-testid="button-submit-status">
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {status ? "Save Changes" : "Add Status"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function LabelFormDialog({ open, onOpenChange, label, onSubmit, isPending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: Label | null;
  onSubmit: (data: z.infer<typeof labelFormSchema>) => void;
  isPending: boolean;
}) {
  const form = useForm<z.infer<typeof labelFormSchema>>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: label?.name ?? "" });
    }
  }, [open, label]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-label-form">
        <DialogHeader>
          <DialogTitle>{label ? "Edit Label" : "Add Label"}</DialogTitle>
          <DialogDescription>
            {label ? "Update the label name." : "Add a new label for categorizing tasks."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Label name" data-testid="input-label-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isPending} data-testid="button-submit-label">
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {label ? "Save Changes" : "Add Label"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function MemberFormDialog({ open, onOpenChange, availableUsers, onSubmit, isPending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableUsers: AvailableUser[];
  onSubmit: (data: z.infer<typeof memberFormSchema>) => void;
  isPending: boolean;
}) {
  const form = useForm<z.infer<typeof memberFormSchema>>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: { userId: "", role: "editor" },
  });

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      form.reset({ userId: "", role: "editor" });
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent data-testid="dialog-member-form">
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
          <DialogDescription>Add a user to this board with a specific role.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="userId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-member-user">
                        <SelectValue placeholder="Select a user..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableUsers.map(u => (
                        <SelectItem key={u.id} value={u.id} data-testid={`select-user-option-${u.id}`}>
                          {u.username} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-member-role">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isPending} data-testid="button-submit-member">
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Member
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

const templateFormSchema = z.object({
  title: z.string().min(1, "Template title is required").max(500),
  description: z.string().max(2000).optional(),
});

function TemplateFormDialog({ open, onOpenChange, onSubmit, isPending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: z.infer<typeof templateFormSchema>) => void;
  isPending: boolean;
}) {
  const form = useForm<z.infer<typeof templateFormSchema>>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: { title: "", description: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ title: "", description: "" });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-template-form">
        <DialogHeader>
          <DialogTitle>New Template</DialogTitle>
          <DialogDescription>
            Create a reusable task template. You can add checklists, labels, and more after creation.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Template title" maxLength={500} data-testid="input-template-title" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Optional description" maxLength={5000} data-testid="input-template-description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isPending} data-testid="button-submit-template">
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Template
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
